import React, { useState } from 'react';
import './HyperparameterSelection.css';

function HyperparameterSelection({ onHyperparametersSelected }) {
  const [hyperparameters, setHyperparameters] = useState({
    use_permutation_importances: false,
    num_trees: 100,
    train_num: 5000,
    max_depth: 30,
    threshold_opt_method: 'bs_frac_cluster',
    n_repeats_perm: 30
  });

  const handleChange = (field, value) => {
    setHyperparameters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onHyperparametersSelected(hyperparameters);
  };

  return (
    <div className="hyperparameter-selection">
      <h2 className="section-title">Configure Model Hyperparameters</h2>
      <p className="section-subtitle">
        Adjust the training parameters for the Random Forest classifier. 
        Default values are recommended for most use cases.
      </p>

      <form onSubmit={handleSubmit} className="hyperparameter-form">
        <div className="hyperparameter-grid">
          {/* Feature Importance Method */}
          <div className="hyperparameter-row">
            <div className="hyperparameter-name">
              Permutation Importances
            </div>
            <div className="hyperparameter-control">
              <label className="hyperparameter-checkbox">
                <input
                  type="checkbox"
                  checked={hyperparameters.use_permutation_importances}
                  onChange={(e) => handleChange('use_permutation_importances', e.target.checked)}
                />
                <span className="checkbox-label">Enable permutation importances</span>
              </label>
              <p className="hyperparameter-description">
                More robust but slower feature importance calculation. 
                Recommended for production models.
              </p>
            </div>
          </div>

          {/* Number of Trees */}
          <div className="hyperparameter-row">
            <div className="hyperparameter-name">
              Number of Trees
            </div>
            <div className="hyperparameter-control">
              <div className="slider-value">{hyperparameters.num_trees}</div>
              <input
                type="range"
                min="2"
                max="100"
                step="1"
                value={hyperparameters.num_trees}
                onChange={(e) => handleChange('num_trees', parseInt(e.target.value))}
                className="hyperparameter-slider"
              />
              <p className="hyperparameter-description">
                Number of decision trees in the ensemble (2-100). 
                More trees = better performance but slower training.
              </p>
            </div>
          </div>

          {/* Training Sample Size */}
          <div className="hyperparameter-row">
            <div className="hyperparameter-name">
              Training Sample Size
            </div>
            <div className="hyperparameter-control">
              <div className="slider-value">{hyperparameters.train_num.toLocaleString()}</div>
              <input
                type="range"
                min="1000"
                max="50000"
                step="1000"
                value={hyperparameters.train_num}
                onChange={(e) => handleChange('train_num', parseInt(e.target.value))}
                className="hyperparameter-slider"
              />
              <p className="hyperparameter-description">
                Number of training samples per tree (1,000-50,000). 
                Should not exceed 50k to maintain balanced sampling.
              </p>
            </div>
          </div>

          {/* Max Depth */}
          <div className="hyperparameter-row">
            <div className="hyperparameter-name">
              Maximum Tree Depth
            </div>
            <div className="hyperparameter-control">
              <div className="slider-value">{hyperparameters.max_depth}</div>
              <input
                type="range"
                min="2"
                max="50"
                step="1"
                value={hyperparameters.max_depth}
                onChange={(e) => handleChange('max_depth', parseInt(e.target.value))}
                className="hyperparameter-slider"
              />
              <p className="hyperparameter-description">
                Maximum depth of each decision tree (2-50). 
                Deeper trees can model complex patterns but may overfit.
              </p>
            </div>
          </div>

          {/* Threshold Optimization Method */}
          <div className="hyperparameter-row">
            <div className="hyperparameter-name">
              Threshold Optimization
            </div>
            <div className="hyperparameter-control">
              <select
                value={hyperparameters.threshold_opt_method}
                onChange={(e) => handleChange('threshold_opt_method', e.target.value)}
                className="hyperparameter-select"
              >
                <option value="bs_frac_cluster">Backsplash Fraction per Cluster</option>
                <option value="bs_frac">Total Backsplash Fraction</option>
                <option value="accuracy">Maximum Accuracy</option>
                <option value="balanced">Balanced (0.5)</option>
              </select>
              <p className="hyperparameter-description">
                {hyperparameters.threshold_opt_method === 'bs_frac_cluster' && 
                  "Minimizes error in backsplash fraction for each cluster (recommended)"}
                {hyperparameters.threshold_opt_method === 'bs_frac' && 
                  "Optimizes total backsplash fraction to match training set (~35%)"}
                {hyperparameters.threshold_opt_method === 'accuracy' && 
                  "Maximizes overall accuracy (biased toward infalling purity)"}
                {hyperparameters.threshold_opt_method === 'balanced' && 
                  "Uses 0.5 threshold, optimal for balanced datasets"}
              </p>
            </div>
          </div>

          {/* Permutation Repeats (conditional) */}
          {hyperparameters.use_permutation_importances && (
            <div className="hyperparameter-row">
              <div className="hyperparameter-name">
                Permutation Repeats
              </div>
              <div className="hyperparameter-control">
                <div className="slider-value">{hyperparameters.n_repeats_perm}</div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="10"
                  value={hyperparameters.n_repeats_perm}
                  onChange={(e) => handleChange('n_repeats_perm', parseInt(e.target.value))}
                  className="hyperparameter-slider"
                />
                <p className="hyperparameter-description">
                  Number of times to repeat permutation for feature importance (10-100). 
                  More repeats = more reliable but slower.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="hyperparameter-actions">
          <button type="submit" className="btn btn-primary">
            Continue to Training
          </button>
        </div>
      </form>
    </div>
  );
}

export default HyperparameterSelection;
